import React, { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Button, Badge } from "@/components/ds";
import { SizeChartTable } from "./SizeChartTable";

const UNIT_FACTOR = { "in>cm": 2.54, "cm>in": 1 / 2.54 };
function convertChart(chart, to) {
  const from = chart.unit;
  if (!from || from === to) return chart;
  const f = UNIT_FACTOR[`${from}>${to}`];
  if (!f) return chart;
  const columns = chart.columns.map((c) => c.replace(new RegExp(`\\(\\s*${from}\\s*\\)`, "ig"), `(${to})`));
  const rows = chart.rows.map((r) => r.map((val, ci) => {
    if (ci === 0) return val;
    const s = String(val).trim();
    const n = parseFloat(s.replace(/[^\d.\-]/g, ""));
    if (s === "" || isNaN(n)) return val;
    return (Math.round(n * f * 10) / 10).toFixed(1);
  }));
  return { ...chart, unit: to, columns, rows };
}

function Slot({ src, index, onOpen }) {
  return (
    <div style={{ position: "relative", flex: "0 0 100%", height: "100%", scrollSnapAlign: "start" }}>
      {src ? (
        <img src={src} alt="" loading="lazy" onClick={onOpen} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block", cursor: "zoom-in" }} />
      ) : (
        <div style={{ width: "100%", height: "100%", background: "var(--lf-blue-900)", backgroundImage: "var(--texture-weave-light)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ font: "var(--fw-medium) 11px/1 var(--font-sans)", color: "var(--lf-blue-200)" }}>Photo {index + 1}</span>
        </div>
      )}
    </div>
  );
}

export default function ProductCard({ p, onQuery, onSample, showMoq, currency = "INR" }) {
  const railRef = useRef(null);
  const [active, setActive] = useState(0);
  const [lightbox, setLightbox] = useState(null);
  const [showChart, setShowChart] = useState(false);
  const [chartUnit, setChartUnit] = useState("in");
  const imgs = (p.images || []).filter(Boolean).slice(0, 4);
  const slots = imgs.length ? imgs : [null];

  const onScroll = () => {
    const rail = railRef.current;
    if (!rail) return;
    const i = Math.round(rail.scrollLeft / Math.max(1, rail.clientWidth));
    if (i !== active) setActive(i);
  };
  const goTo = (i) => railRef.current?.scrollTo({ left: railRef.current.clientWidth * i, behavior: "smooth" });

  const moqLabel = showMoq ? p.moqLabel : "On request";

  const Cell = ({ label, value, mono }) => (
    <div>
      <div style={{ font: "var(--fw-semibold) 10px/1 var(--font-sans)", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-tertiary)", marginBottom: 4 }}>{label}</div>
      <div style={{ font: `var(--fw-medium) 12px/1.2 ${mono ? "var(--font-mono)" : "var(--font-sans)"}`, color: "var(--text-primary)" }}>{value}</div>
    </div>
  );

  return (
    <article
      data-testid={`product-card-${p.code}`}
      style={{ display: "flex", flexDirection: "column", background: "var(--surface-card)", border: "1px solid var(--border-default)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-soft)", overflow: "hidden", transition: "box-shadow var(--dur-base), transform var(--dur-base) var(--ease-out)" }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-floating)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-soft)"; e.currentTarget.style.transform = "none"; }}
    >
      <div style={{ position: "relative", background: "var(--surface-inset)" }}>
        <div style={{ position: "relative", aspectRatio: "3 / 4" }}>
          <div ref={railRef} data-carousel="true" onScroll={onScroll} style={{ display: "flex", height: "100%", overflowX: "auto", overflowY: "hidden", scrollSnapType: "x mandatory", scrollBehavior: "smooth", scrollbarWidth: "none" }}>
            {slots.map((s, i) => <Slot key={i} src={s} index={i} onOpen={() => setLightbox(i)} />)}
          </div>
          {slots.length > 1 && (
            <div style={{ position: "absolute", bottom: 10, left: 0, right: 0, display: "flex", justifyContent: "center", gap: 6 }}>
              {slots.map((_, i) => (
                <span key={i} data-testid={`dot-${p.code}-${i}`} onClick={() => goTo(i)} style={{ width: 7, height: 7, borderRadius: "50%", background: i === active ? "var(--lf-blue-600)" : "var(--lf-slate-300)", boxShadow: "0 0 0 1.5px rgba(255,255,255,0.9)", cursor: "pointer", transition: "background-color var(--dur-fast)" }} />
              ))}
            </div>
          )}
          {p.matchScore != null && (
            <div data-testid={`match-pill-${p.code}`} style={{ position: "absolute", top: 12, left: 12, pointerEvents: "none", padding: "4px 9px", borderRadius: "var(--radius-pill)", background: "var(--lf-blue-600)", color: "#fff", font: "var(--fw-semibold) 11px/1 var(--font-mono)" }}>{p.matchScore}% match</div>
          )}
          {p.badge && (
            <div style={{ position: "absolute", top: 12, right: 12, pointerEvents: "none" }}>
              <Badge tone={p.badgeTone}>{p.badge}</Badge>
            </div>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 10, padding: "12px 12px 14px", flex: 1 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ font: "var(--fw-semibold) 11px/1 var(--font-sans)", letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--lf-blue-600)" }}>{p.categoryLabel}</span>
          <h3 style={{ margin: 0, font: "var(--fw-medium) 15px/1.3 var(--font-display)", color: "var(--text-primary)" }}>{p.name}</h3>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(2,minmax(0,1fr))", gap: 8 }}>
          <Cell label="Fabric" value={p.fabric} />
          {p.weight && <Cell label="Weight" value={p.weight} mono />}
          <Cell label="Sizes" value="28–40 available" mono />
        </div>

        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 10, borderTop: "1px solid var(--border-default)", paddingTop: 12 }}>
          <div>
            <div style={{ font: "var(--fw-semibold) 18px/1 var(--font-mono)", color: "var(--text-primary)" }}>{p.priceLabel}</div>
            <div style={{ marginTop: 5, font: "var(--fw-regular) 11px/1 var(--font-sans)", color: "var(--text-tertiary)" }}>{currency === "USD" ? "USD / pc · ex-factory" : "per piece · ex-factory"}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ font: "var(--fw-semibold) 14px/1 var(--font-mono)", color: showMoq ? "var(--lf-emerald-600)" : "var(--text-secondary)" }}>{moqLabel}</div>
            <div style={{ marginTop: 5, font: "var(--fw-regular) 11px/1 var(--font-sans)", color: "var(--text-tertiary)" }}>min. order</div>
          </div>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: "auto" }}>
          {p.size_chart && p.size_chart.columns && (
            <button data-testid={`size-chart-btn-${p.code}`} onClick={() => { setChartUnit(p.size_chart.unit === "cm" ? "cm" : "in"); setShowChart(true); }} style={{ display: "inline-flex", alignItems: "center", gap: 6, alignSelf: "flex-start", background: "none", border: "none", padding: 0, cursor: "pointer", font: "var(--fw-semibold) 12px/1 var(--font-sans)", color: "var(--lf-blue-600)" }}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="16" rx="2" /><path d="M3 10h18M9 4v16" /></svg>
              Size chart
            </button>
          )}
          <Button data-testid={`sample-btn-${p.code}`} variant="primary" size="sm" fullWidth onClick={() => onSample(p)}>Request sample</Button>
        </div>
      </div>

      {lightbox !== null && imgs.length > 0 && createPortal(
        <div data-testid={`lightbox-${p.code}`} onClick={() => setLightbox(null)} style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(20,19,28,0.92)", display: "flex", alignItems: "center", justifyContent: "center", padding: 40 }}>
          <img src={imgs[lightbox]} alt="" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "90vw", maxHeight: "88vh", objectFit: "contain", borderRadius: "var(--radius-lg)", boxShadow: "var(--shadow-overlay)" }} />
          <button data-testid={`lightbox-close-${p.code}`} onClick={() => setLightbox(null)} style={{ position: "absolute", top: 24, right: 28, width: 40, height: 40, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", fontSize: 24, lineHeight: 1, cursor: "pointer" }}>×</button>
          {imgs.length > 1 && (
            <>
              <button onClick={(e) => { e.stopPropagation(); setLightbox((c) => (c - 1 + imgs.length) % imgs.length); }} style={{ position: "absolute", left: 24, top: "50%", transform: "translateY(-50%)", width: 46, height: 46, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", fontSize: 26, lineHeight: 1, cursor: "pointer" }}>‹</button>
              <button onClick={(e) => { e.stopPropagation(); setLightbox((c) => (c + 1) % imgs.length); }} style={{ position: "absolute", right: 24, top: "50%", transform: "translateY(-50%)", width: 46, height: 46, borderRadius: "50%", border: "none", background: "rgba(255,255,255,0.14)", color: "#fff", fontSize: 26, lineHeight: 1, cursor: "pointer" }}>›</button>
              <div style={{ position: "absolute", bottom: 28, left: 0, right: 0, textAlign: "center", color: "rgba(255,255,255,0.7)", font: "var(--fw-medium) 13px/1 var(--font-mono)" }}>{lightbox + 1} / {imgs.length}</div>
            </>
          )}
        </div>, document.body)}

      {showChart && p.size_chart && createPortal(
        <div data-testid={`size-chart-modal-${p.code}`} onClick={() => setShowChart(false)} style={{ position: "fixed", inset: 0, zIndex: 95, background: "rgba(20,19,28,0.6)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface-card)", borderRadius: "var(--radius-xl)", boxShadow: "var(--shadow-overlay)", maxWidth: 760, width: "100%", maxHeight: "85vh", overflow: "auto", padding: 24 }}>
            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 16, marginBottom: 16 }}>
              <div>
                <div style={{ font: "var(--fw-semibold) 17px/1.2 var(--font-display)", color: "var(--text-primary)" }}>Size chart · {p.name}</div>
                <div style={{ marginTop: 4, font: "var(--fw-regular) 12px/1.3 var(--font-sans)", color: "var(--text-tertiary)" }}>{["in", "cm"].includes(p.size_chart.unit) ? `Measurements in ${chartUnit === "cm" ? "centimetres" : "inches"}` : (p.size_chart.unit ? `Measurements in ${p.size_chart.unit}` : "Measurements as specified")}</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {["in", "cm"].includes(p.size_chart.unit) && (
                  <div data-testid={`size-chart-unit-toggle-${p.code}`} style={{ display: "inline-flex", padding: 3, borderRadius: "var(--radius-pill)", background: "var(--surface-muted)", border: "1px solid var(--border-default)" }}>
                    {["in", "cm"].map((u) => (
                      <button key={u} data-testid={`size-chart-unit-${u}-${p.code}`} onClick={() => setChartUnit(u)} style={{ padding: "5px 14px", borderRadius: "var(--radius-pill)", border: "none", cursor: "pointer", font: "var(--fw-semibold) 12px/1 var(--font-sans)", background: chartUnit === u ? "var(--lf-blue-600)" : "transparent", color: chartUnit === u ? "#fff" : "var(--text-secondary)", transition: "background-color var(--dur-fast), color var(--dur-fast)" }}>{u}</button>
                    ))}
                  </div>
                )}
                <button data-testid={`size-chart-close-${p.code}`} onClick={() => setShowChart(false)} style={{ flexShrink: 0, width: 34, height: 34, borderRadius: "50%", border: "1px solid var(--border-strong)", background: "var(--surface-card)", color: "var(--text-secondary)", fontSize: 20, lineHeight: 1, cursor: "pointer" }}>×</button>
              </div>
            </div>
            <SizeChartTable chart={convertChart(p.size_chart, chartUnit)} />
          </div>
        </div>, document.body)}
    </article>
  );
}
